import React, {useEffect, useMemo, useRef, useState} from "react";
import {FormikProps, FormikProvider, useFormik} from "formik";
import {useSelector} from "react-redux";
import {Template} from "@reside/forms";
import {
  BlockField,
  Card,
  Icon,
  QueryVariableMap,
  styled,
  Subtitle,
} from "@reside/ui";

import {AppState, select} from "../../../../store";
import {
  FacilitySettingsScrollTarget,
  FacilitySettingsSection,
} from "./FacilitySettingsSection";
import {
  makeWorkflowBlockSectionId,
  makeWorkflowDocumentCategorySectionId,
  makeWorkflowDownloadEmptyPacketSectionId,
  makeWorkflowSectionId,
} from "./workflowSectionIds";
import {
  extractWorkflowSettingsBlocks,
  getWorkflowDownloadEmptyPacketTitle,
} from "./workflowSettingsBlocks";
import {DocumentCategoryCardSaveButton} from "./forms/DocumentCategoryCardSaveButton";
import {WorkflowDocumentCategoryField} from "./forms/WorkflowDocumentCategoryField";
import {WorkflowDownloadEmptyPacketButton} from "./forms/WorkflowDownloadEmptyPacketButton";
import {useTemplateVersionQuery} from "../../../../hooks/useTemplateVersionQuery";
import {useWorkflowFacilitySettingsTemplateQuery} from "../../../../hooks/useTemplateQuery";
import {useFacilitySettingsParams} from "../../hooks/useFacilitySettingsParams";
import {
  FacilitySettingsVariables,
  WorkflowSettingsVariables,
} from "../../../../services/FacilitySettingsService";
import {DownloadArbitrationPDFButton} from "./forms/DownloadArbitrationPDFButton";
import {useTemplateHasArbitrationDocumentQuery} from "../../../../models/pcc";
import {WorkflowType} from "../../../../utils/enums";

type Props = Readonly<{
  workflowType: string;
  workflowLabel: string;
  facilityTemplate: string;
  isPccEnabled?: boolean;
  queryVariables?: QueryVariableMap;
  facilitySettingsFormik: FormikProps<FacilitySettingsVariables>;
}>;

export const WorkflowSettingsSection = ({
  workflowType,
  workflowLabel,
  facilityTemplate,
  isPccEnabled,
  queryVariables,
  facilitySettingsFormik,
}: Props) => {
  const {facilityId} = useFacilitySettingsParams();
  const isResideAdmin = useSelector((state: AppState) =>
    select.adminSession.isResideAdmin(state),
  );
  const {data: templateVersionData} = useTemplateVersionQuery({facilityId});
  const versionId = (templateVersionData?.version as {id?: string} | undefined)
    ?.id;

  const {data: workflowTemplate, isLoading: isLoadingWorkflowTemplate} =
    useWorkflowFacilitySettingsTemplateQuery({
      versionId,
      workflowType,
    });

  const allWorkflowTypes: string[] =
    (templateVersionData?.version as {workflowTypes?: string[]} | undefined)
      ?.workflowTypes ?? [];

  const hasArbitrationWorkflow = allWorkflowTypes.some(
    wf => wf.toLowerCase() === WorkflowType.ARBITRATION.toLowerCase(),
  );

  const {
    data: templateArbitrationMeta,
    isLoading: isLoadingTemplateArbitration,
  } = useTemplateHasArbitrationDocumentQuery(facilityId, facilityTemplate, {
    enabled: !!facilityId && !!facilityTemplate,
  });

  const normalizedType = workflowType.toLowerCase();
  const showAdmissionEmptyArbitrationDownload =
    normalizedType === WorkflowType.ADMISSION &&
    !isLoadingTemplateArbitration &&
    templateArbitrationMeta?.hasArbitrationDocument === true;
  const showsCategoryField =
    Boolean(isPccEnabled) &&
    (normalizedType === WorkflowType.ADMISSION ||
      (normalizedType === WorkflowType.ARBITRATION && hasArbitrationWorkflow) ||
      (normalizedType !== WorkflowType.ADMISSION &&
        normalizedType !== WorkflowType.ARBITRATION &&
        allWorkflowTypes.some(wf => wf.toLowerCase() === normalizedType)));

  // The workflow's own fields (e.g. its checklist) live in a Formik instance
  // separate from the parent form. This is required because the workflow
  // template's fieldArray shares the literal id "checklist" with the
  // admission template's fieldArray - if both bound to the same Formik
  // instance, their values would collide into one shared array. See
  // WorkflowSettingsSection's git history / DEV plan for the full rationale
  // (BlockFieldArray in @reside-admissions/ui reads values/errors/touched by
  // plain bracket access on the field's id, not via Formik's dot-path-aware
  // getIn, so a dotted id would break that component instead of fixing this).
  const parentWorkflowsMap = facilitySettingsFormik.values.workflows as
    | Record<string, WorkflowSettingsVariables>
    | undefined;

  const [initialWorkflowValues] = useState<WorkflowSettingsVariables>(
    () => parentWorkflowsMap?.[workflowType] ?? {},
  );

  const workflowFormik = useFormik<WorkflowSettingsVariables>({
    initialValues: initialWorkflowValues,
    onSubmit: () => undefined,
  });

  // Tracks the last value we've reconciled between local <-> parent state,
  // so the two effects below don't loop against each other.
  const lastSyncedValueRef = useRef(JSON.stringify(initialWorkflowValues));

  useEffect(() => {
    const serialized = JSON.stringify(workflowFormik.values);
    if (lastSyncedValueRef.current === serialized) {
      return;
    }
    lastSyncedValueRef.current = serialized;
    facilitySettingsFormik.setFieldValue(
      `workflows.${workflowType}`,
      workflowFormik.values,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowFormik.values]);

  useEffect(() => {
    const parentValue = parentWorkflowsMap?.[workflowType];
    const serialized = JSON.stringify(parentValue ?? {});
    if (lastSyncedValueRef.current === serialized) {
      return;
    }
    lastSyncedValueRef.current = serialized;
    workflowFormik.resetForm({values: parentValue ?? {}});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentWorkflowsMap?.[workflowType]]);

  // Visibility rules inside the workflow template (e.g. documentUploadEnabled)
  // can reference both workflow-local fields and cross-cutting admission
  // fields (isPccEnabled, pccSettings), so both need to be resolvable here.
  const mergedAnswers = useMemo(
    () => ({...facilitySettingsFormik.values, ...workflowFormik.values}),
    [facilitySettingsFormik.values, workflowFormik.values],
  );

  const providerValue = useMemo(
    () => ({
      ...workflowFormik,
      values: mergedAnswers,
      errors: {...facilitySettingsFormik.errors, ...workflowFormik.errors},
      touched: {...facilitySettingsFormik.touched, ...workflowFormik.touched},
    }),
    [
      workflowFormik,
      mergedAnswers,
      facilitySettingsFormik.errors,
      facilitySettingsFormik.touched,
    ],
  );

  const workflowBlocks = useMemo(
    () =>
      extractWorkflowSettingsBlocks(
        workflowTemplate as Template | undefined,
        mergedAnswers,
        isResideAdmin,
      ),
    [workflowTemplate, mergedAnswers, isResideAdmin],
  );

  return (
    <FacilitySettingsSection id={makeWorkflowSectionId(workflowType)}>
      <SectionHeading>{workflowLabel}</SectionHeading>

      {isLoadingWorkflowTemplate &&
      normalizedType !== WorkflowType.ADMISSION ? (
        <EmptyNote>Loading workflow settings…</EmptyNote>
      ) : null}

      <FormikProvider value={providerValue as any}>
        {workflowBlocks.map(block => (
          <FacilitySettingsScrollTarget
            key={String(block.id)}
            id={makeWorkflowBlockSectionId(workflowType, String(block.id))}
          >
            <BlockField block={block} queryVariables={queryVariables} />
          </FacilitySettingsScrollTarget>
        ))}
      </FormikProvider>

      {isResideAdmin ? (
        <FacilitySettingsScrollTarget
          id={makeWorkflowDocumentCategorySectionId(workflowType)}
        >
          {showsCategoryField ? (
            <StyledCard>
              <Subtitle>Document Upload Category</Subtitle>
              <CategoryContent>
                <WorkflowDocumentCategoryField
                  workflowType={workflowType}
                  isPccEnabled={isPccEnabled}
                  hasArbitrationWorkflow={hasArbitrationWorkflow}
                />
                <DocumentCategoryCardSaveButton
                  workflowType={workflowType}
                  hasArbitrationWorkflow={hasArbitrationWorkflow}
                />
              </CategoryContent>
            </StyledCard>
          ) : (
            <StyledCard>
              <Subtitle>Document Upload Category</Subtitle>
              <CategoryContent>
                <PccCategoryNoticeRow>
                  <PccCategoryNoticeIcon name="alert" size={20} />
                  <PccCategoryNoticeText>
                    PointClickCare document upload categories are available when
                    PCC is enabled for this facility.
                  </PccCategoryNoticeText>
                </PccCategoryNoticeRow>
              </CategoryContent>
            </StyledCard>
          )}
        </FacilitySettingsScrollTarget>
      ) : null}

      <FacilitySettingsScrollTarget
        id={makeWorkflowDownloadEmptyPacketSectionId(workflowType)}
      >
        <StyledCard>
          <Subtitle>
            {getWorkflowDownloadEmptyPacketTitle(workflowLabel)}
          </Subtitle>
          <DownloadActions>
            <WorkflowDownloadEmptyPacketButton
              facilityTemplate={facilityTemplate}
              workflowType={workflowType}
              downloadTitle={getWorkflowDownloadEmptyPacketTitle(workflowLabel)}
            />
          </DownloadActions>
        </StyledCard>
      </FacilitySettingsScrollTarget>

      {showAdmissionEmptyArbitrationDownload ? (
        <FacilitySettingsScrollTarget
          id={`${makeWorkflowDownloadEmptyPacketSectionId(
            workflowType,
          )}:arbitration`}
        >
          <StyledCard>
            <Subtitle>Download Empty Arbitration Packet</Subtitle>
            <DownloadActions>
              {/* Hillwaley facility still has old 888 arbitration handling, so we need to download the empty arbitration packet like this. */}
              <DownloadArbitrationPDFButton
                facilityTemplate={facilityTemplate}
                type="empty"
              />
            </DownloadActions>
          </StyledCard>
        </FacilitySettingsScrollTarget>
      ) : null}
    </FacilitySettingsSection>
  );
};

const SectionHeading = styled.h3`
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 600;
  color: ${({theme}) => theme.color.darkBlue100};
`;

const StyledCard = styled(Card)`
  padding: 24px;
  margin-bottom: 16px;
`;

const DownloadActions = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 16px;
`;

const CategoryContent = styled.div`
  margin-top: 16px;
`;

const EmptyNote = styled.p`
  padding: 24px 16px;
  font-size: 13px;
  color: ${({theme}) => theme.color.darkBlue100};
  opacity: 0.5;
  font-style: italic;
  margin: 0;
`;

const PccCategoryNoticeRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const PccCategoryNoticeIcon = styled(Icon)`
  flex-shrink: 0;
  margin-top: 2px;
  color: ${({theme}) => theme.color.orange100};
`;

const PccCategoryNoticeText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({theme}) => theme.color.darkBlue100};
`;
